export interface TrimOptions {
  aspectRatio?: string;
  format?: 'mp4' | 'webm';
  onProgress?: (progressText: string) => void;
}

export async function trimVideo(
  source: string | File | Blob,
  startTime: number,
  endTime: number,
  options: TrimOptions = {}
): Promise<Blob> {
  if (endTime <= startTime) {
    throw new Error('O tempo final do recorte deve ser maior que o tempo inicial.');
  }

  const isBlobOrFile = typeof source !== 'string';
  const videoUrl = isBlobOrFile ? URL.createObjectURL(source) : source;

  let width = 720;
  let height = 1280;
  const ratio = options.aspectRatio || '9:16';
  if (ratio === '1:1') {
    width = 720;
    height = 720;
  } else if (ratio === '16:9') {
    width = 1280;
    height = 720;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Não foi possível inicializar o canvas de recorte.');

  const stream = canvas.captureStream(30);

  const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
  let audioCtx: AudioContext | null = null;
  let audioDest: MediaStreamAudioDestinationNode | null = null;
  if (AudioContextClass) {
    try {
      audioCtx = new AudioContextClass();
      audioDest = audioCtx.createMediaStreamDestination();
    } catch (_) {}
  }

  const tracks: MediaStreamTrack[] = [...stream.getVideoTracks()];
  if (audioDest) tracks.push(...audioDest.stream.getAudioTracks());
  const combinedStream = new MediaStream(tracks);

  let mimeType = 'video/mp4';
  if (options.format === 'webm') {
    if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
      mimeType = 'video/webm;codecs=vp9,opus';
    } else {
      mimeType = 'video/webm';
    }
  } else {
    if (MediaRecorder.isTypeSupported('video/mp4;codecs=avc1,mp4a.40.2')) {
      mimeType = 'video/mp4;codecs=avc1,mp4a.40.2';
    } else if (MediaRecorder.isTypeSupported('video/mp4')) {
      mimeType = 'video/mp4';
    } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
      mimeType = 'video/webm;codecs=vp9,opus';
    } else {
      mimeType = 'video/webm';
    }
  }

  const chunks: Blob[] = [];
  const recorder = new MediaRecorder(combinedStream, {
    mimeType,
    videoBitsPerSecond: 4_500_000,
  });

  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };

  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.src = videoUrl;
  video.muted = false;
  video.playsInline = true;

  return new Promise<Blob>((resolve, reject) => {
    let animId: number | null = null;
    let isFinished = false;

    const cleanup = () => {
      isFinished = true;
      if (animId) cancelAnimationFrame(animId);
      video.pause();
      video.removeAttribute('src');
      video.load();
      if (isBlobOrFile) URL.revokeObjectURL(videoUrl);
      if (audioCtx) audioCtx.close().catch(() => {});
    };

    const renderLoop = () => {
      if (isFinished) return;

      if (video.currentTime >= endTime || video.ended) {
        isFinished = true;
        video.pause();
        setTimeout(() => {
          if (recorder.state === 'recording') recorder.stop();
        }, 100);
        return;
      }

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, width, height);

      const vRatio = video.videoWidth / video.videoHeight;
      const cRatio = width / height;
      let dw = width;
      let dh = height;
      let dx = 0;
      let dy = 0;

      if (vRatio > cRatio) {
        dh = width / vRatio;
        dy = (height - dh) / 2;
      } else {
        dw = height * vRatio;
        dx = (width - dw) / 2;
      }

      ctx.drawImage(video, dx, dy, dw, dh);

      const currentProg = (video.currentTime - startTime) / (endTime - startTime);
      if (options.onProgress) {
        options.onProgress(`Recortando: ${Math.max(0, Math.min(100, Math.round(currentProg * 100)))}%`);
      }

      animId = requestAnimationFrame(renderLoop);
    };

    video.onplaying = () => {
      if (isFinished) return;
      if (recorder.state === 'inactive') {
        recorder.start(100);
      }
      if (!animId) {
        animId = requestAnimationFrame(renderLoop);
      }
    };

    video.onloadedmetadata = async () => {
      try {
        if (audioCtx && audioDest) {
          if (audioCtx.state === 'suspended') await audioCtx.resume();
          const sourceNode = audioCtx.createMediaElementSource(video);
          sourceNode.connect(audioDest);
        }
      } catch (_) {}

      if (startTime > 0) {
        video.currentTime = startTime;
        video.onseeked = () => {
          video.play().catch((err) => {
            cleanup();
            reject(err);
          });
        };
      } else {
        video.play().catch((err) => {
          cleanup();
          reject(err);
        });
      }
    };

    recorder.onstop = () => {
      cleanup();
      const outputBlob = new Blob(chunks, { type: mimeType });
      resolve(outputBlob);
    };

    video.onerror = () => {
      cleanup();
      reject(new Error('Erro ao carregar o vídeo para recorte. Verifique o formato ou permissão CORS.'));
    };
  });
}
